import {
  Schedule,
  Rule,
  AbsoluteRangeRuleSchema,
  WeeklyPatternRuleSchema,
  RotatingPatternRuleSchema,
  FeatureSchema,
} from "../types";
import {
  AddFeatureOperation,
  AddRuleOperation,
  ModifyFeatureOperation,
  ModifyRuleOperation,
  RemoveFeatureOperation,
  RemoveRuleOperation,
  ReorderRulesOperation,
} from "./types";
import { omit } from "es-toolkit";

type OperationResult =
  | {
      success: true;
      errors: [];
      schedule: Schedule;
    }
  | {
      success: false;
      errors: string[];
      schedule: null;
    };

const _errorResult = (err: string): OperationResult => ({
  success: false,
  errors: [err],
  schedule: null,
});

export function applyAddFeature(schedule: Schedule, op: AddFeatureOperation): OperationResult {
  const name = op.data.name;
  if (name in schedule.features) {
    return _errorResult(`Cannot add feature: a feature named ${name} already exists`);
  }
  schedule.features = {
    ...schedule.features,
    [name]: FeatureSchema.decode(op.data),
  };
  return {
    success: true,
    errors: [],
    schedule,
  };
}

export function applyModifyFeature(schedule: Schedule, op: ModifyFeatureOperation): OperationResult {
  const name = op.data.name;
  const existing = schedule.features[name];
  if (!existing) {
    return _errorResult(`Cannot modify feature: a feature named ${name} does not exist`);
  }

  const updated = FeatureSchema.decode({
    ...existing,
    ...Object.fromEntries(Object.entries(op.data).filter(([, v]) => v !== undefined)),
  });

  schedule.features = {
    ...schedule.features,
    [name]: updated,
  };
  return {
    success: true,
    errors: [],
    schedule,
  };
}

export function applyRemoveFeature(schedule: Schedule, op: RemoveFeatureOperation): OperationResult {
  const name = op.data.name;
  if (!(name in schedule.features)) {
    return _errorResult(`Cannot remove feature: a feature named ${name} does not exist`);
  }
  for (const rule of schedule.rules) {
    if (name in rule.features) {
      return _errorResult(`Cannot remove feature: a feature named ${name} is referenced by rule ${rule.id}`);
    }
  }
  schedule.features = omit(schedule.features, [name]);
  return {
    success: true,
    errors: [],
    schedule,
  };
}

export function applyAddRule(schedule: Schedule, op: AddRuleOperation): OperationResult {
  const ruleId = op.data.id;
  if (schedule.rules.some((rule) => rule.id === ruleId)) {
    return _errorResult(`Cannot add rule: a rule with id ${ruleId} already exists`);
  }

  let rule;
  const ruleKind = op.data.kind;
  switch (ruleKind) {
    case "absolute_range": {
      rule = AbsoluteRangeRuleSchema.decode(op.data);
      break;
    }
    case "weekly_pattern": {
      rule = WeeklyPatternRuleSchema.decode(op.data);
      break;
    }
    case "rotating_pattern": {
      rule = RotatingPatternRuleSchema.decode(op.data);
      break;
    }
    default: {
      const _exhaustive: never = ruleKind;
      return {
        success: false,
        errors: [`Cannot add rule: unknown kind of rule. ${JSON.stringify(_exhaustive)}`],
        schedule: null,
      };
    }
  }
  schedule.rules.push(rule);
  return {
    success: true,
    errors: [],
    schedule,
  };
}

export function applyModifyRule(schedule: Schedule, op: ModifyRuleOperation): OperationResult {
  const ruleId = op.data.id;
  const currentRule = schedule.rules.find((rule) => rule.id === ruleId);
  if (!currentRule) {
    return _errorResult(`Cannot modify rule: a rule with id ${ruleId} does not exist`);
  }
  if (currentRule.kind !== op.data.kind) {
    return _errorResult(`Cannot modify rule: rule kind ${op.data.kind} does not match known kind ${currentRule.kind}`);
  }

  let modifiedRule: Rule;
  switch (currentRule.kind) {
    case "absolute_range": {
      modifiedRule = AbsoluteRangeRuleSchema.decode({
        ...currentRule,
        ...Object.fromEntries(Object.entries(op.data).filter(([, v]) => v !== undefined)),
      });
      break;
    }
    case "weekly_pattern": {
      modifiedRule = WeeklyPatternRuleSchema.decode({
        ...currentRule,
        ...Object.fromEntries(Object.entries(op.data).filter(([, v]) => v !== undefined)),
      });
      break;
    }
    case "rotating_pattern": {
      modifiedRule = RotatingPatternRuleSchema.decode({
        ...currentRule,
        ...Object.fromEntries(Object.entries(op.data).filter(([, v]) => v !== undefined)),
      });
      break;
    }
    default: {
      const _exhaustive: never = currentRule;
      return {
        success: false,
        errors: [`Cannot modify rule: unknown kind of rule. ${JSON.stringify(_exhaustive)}`],
        schedule: null,
      };
    }
  }
  schedule.rules = schedule.rules.map((rule) => (rule.id === modifiedRule.id ? modifiedRule : rule));
  return {
    success: true,
    errors: [],
    schedule,
  };
}

export function applyRemoveRule(schedule: Schedule, op: RemoveRuleOperation): OperationResult {
  const ruleId = op.data.id;
  if (!schedule.rules.some((rule) => rule.id === ruleId)) {
    return _errorResult(`Cannot remove rule: a rule with id ${ruleId} does not exist`);
  }
  schedule.rules = schedule.rules.filter((rule) => rule.id !== ruleId);
  return {
    success: true,
    errors: [],
    schedule,
  };
}

export function applyReorderRules(schedule: Schedule, op: ReorderRulesOperation): OperationResult {
  const ruleIdsToReorder = new Set(op.data.rule_ids);
  const rulesById: Map<string, Rule> = new Map();
  for (const rule of schedule.rules) {
    rulesById.set(rule.id, rule);
    if (!ruleIdsToReorder.has(rule.id)) {
      return _errorResult(`Cannot reorder rules: the order does not include the rule ${rule.id}`);
    }
  }
  const reordered: Rule[] = [];
  for (const ruleId of op.data.rule_ids) {
    const rule = rulesById.get(ruleId);
    if (!rule) {
      return _errorResult(`Cannot reorder rules: a rule with id ${ruleId} does not exist`);
    }
    reordered.push(rule);
  }
  schedule.rules = reordered;
  return {
    success: true,
    errors: [],
    schedule,
  };
}
